import { useMemo } from 'react';
import { Entity, RELATION_OWNED_BY } from '@mainstage/catalog-model';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import { buildReservedFieldError } from '../../ReservedFieldsError/ReservedFieldsError';
import {
  getEntityRelations,
  humanizeEntityRef,
} from '@mainstage/plugin-catalog-react';
import { extractPartOfSystemRelations } from '../utils';

type SystemNameMap = Map<string, string>;

interface Props {
  entities: Entity[];
  errors: EntityErrorResult[] | undefined;
  systemsNameMap?: SystemNameMap;
}

export function useRows({ entities, errors, systemsNameMap }: Props) {
  const rows = useMemo(
    () =>
      entities.map(entity => {
        const entityError: string | undefined = (errors ?? []).find(
          e => e.entity_id === entity.metadata.uid,
        )?.errorMessage;

        const reservedFieldError = buildReservedFieldError(entity);

        const partOfSystemRelations = extractPartOfSystemRelations(entity);
        const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

        return {
          entity,
          error: entityError || reservedFieldError,
          resolved: {
            name: humanizeEntityRef(entity, {
              defaultKind: 'Component',
            }),
            ownedByRelationsTitle: ownedByRelations
              .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
              .join(', '),
            ownedByRelations,
            partOfSystemRelationTitle: partOfSystemRelations
              // compare system names in a case-insensitive way becasue of possible casing differences between relation names and actual entity names
              .map(r => systemsNameMap?.get(r.name.toLowerCase()) || r.name)
              .join(', '),
            partOfSystemRelations,
          },
        };
      }),
    [entities, errors, systemsNameMap],
  );

  return rows;
}
